import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, STATUS_LABEL, STATUS_BADGE, formatApiErrorDetail } from "@/lib/api";
import { Plus, ChevronLeft, ChevronRight, Filter, Lock, AlertTriangle } from "lucide-react";
import AppointmentSheet from "@/components/AppointmentSheet";
import { toast } from "sonner";

const STATUSES = ["pendiente", "confirmada", "en_atencion", "finalizada", "cancelada"];

function startOfWeek(d) {
  const date = new Date(d);
  const day = (date.getDay() + 6) % 7; // Monday=0
  date.setDate(date.getDate() - day);
  date.setHours(0, 0, 0, 0);
  return date;
}
function localDateKey(d) {
  const x = new Date(d);
  const y = x.getFullYear();
  const m = String(x.getMonth() + 1).padStart(2, "0");
  const day = String(x.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function fmtDay(d) {
  return d.toLocaleDateString("es-ES", { weekday: "short", day: "numeric" });
}
function fmtRange(a, b) {
  return `${a.toLocaleDateString("es-ES", { day: "numeric", month: "short" })} – ${b.toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" })}`;
}

export default function Calendario() {
  const [view, setView] = useState(() => (typeof window !== "undefined" && window.innerWidth < 768 ? "day" : "week"));
  const [cursor, setCursor] = useState(new Date());
  const [appts, setAppts] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [treatments, setTreatments] = useState([]);
  const [filterDoc, setFilterDoc] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [editing, setEditing] = useState(null); // null | {} | object
  const [sheetOpen, setSheetOpen] = useState(false);
  const [cashOpen, setCashOpen] = useState(true);
  const [settings, setSettings] = useState(null);

  useEffect(() => { api.get("/clinic-settings").then((r) => setSettings(r.data)).catch(() => {}); }, []);

  const dayKeyOf = (d) => ["sun","mon","tue","wed","thu","fri","sat"][new Date(d).getDay()];
  const isDayOpen = (d) => {
    if (!settings?.schedule) return true;
    const ds = settings.schedule[dayKeyOf(d)];
    return !!ds?.enabled;
  };

  useEffect(() => {
    api.get("/cashbox/today")
      .then((r) => setCashOpen(!!(r.data.session && r.data.session.status === "abierta")))
      .catch(() => {});
  }, [sheetOpen]);

  const range = useMemo(() => {
    const d = new Date(cursor);
    if (view === "day") {
      const start = new Date(d); start.setHours(0,0,0,0);
      const end = new Date(d); end.setHours(23,59,59,999);
      return { start, end };
    }
    if (view === "week") {
      const start = startOfWeek(d);
      const end = new Date(start); end.setDate(end.getDate() + 6); end.setHours(23,59,59,999);
      return { start, end };
    }
    const start = new Date(d.getFullYear(), d.getMonth(), 1);
    const end = new Date(d.getFullYear(), d.getMonth()+1, 0); end.setHours(23,59,59,999);
    return { start, end };
  }, [view, cursor]);

  const load = async () => {
    const params = {
      start: range.start.toISOString(),
      end: range.end.toISOString(),
    };
    if (filterDoc) params.doctor_id = filterDoc;
    if (filterStatus) params.status = filterStatus;
    const [{ data: a }, { data: d }, { data: t }] = await Promise.all([
      api.get("/appointments", { params }),
      api.get("/users", { params: { role: "odontologo" } }),
      api.get("/treatments"),
    ]);
    setAppts(a); setDoctors(d); setTreatments(t);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [range.start.toISOString(), range.end.toISOString(), filterDoc, filterStatus]);

  const move = (n) => {
    const d = new Date(cursor);
    if (view === "day") d.setDate(d.getDate() + n);
    else if (view === "week") d.setDate(d.getDate() + 7 * n);
    else d.setMonth(d.getMonth() + n);
    setCursor(d);
  };

  const updateStatus = async (id, status, paymentMethod, amount) => {
    try {
      const params = { status };
      if (paymentMethod) params.payment_method = paymentMethod;
      if (amount != null && amount !== "") params.amount = amount;
      await api.patch(`/appointments/${id}/status`, null, { params });
      toast.success(status === "finalizada" ? "Cita finalizada y cobrada" : "Estado actualizado");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo actualizar");
    }
  };

  const days = useMemo(() => {
    const arr = [];
    const start = new Date(range.start);
    const numDays = view === "day" ? 1 : view === "week" ? 7 : new Date(start.getFullYear(), start.getMonth()+1, 0).getDate();
    for (let i = 0; i < numDays; i++) {
      const d = new Date(start); d.setDate(start.getDate() + i);
      arr.push(d);
    }
    return arr;
  }, [range, view]);

  const apptsByDay = useMemo(() => {
    const m = {};
    for (const a of appts) {
      const k = localDateKey(a.start);
      (m[k] = m[k] || []).push(a);
    }
    return m;
  }, [appts]);

  const openNew = () => {
    const today = new Date();
    if (!isDayOpen(today)) {
      toast.error("Hoy la clínica no atiende según el horario configurado.");
      return;
    }
    setEditing({}); setSheetOpen(true);
  };
  const openEdit = (a) => { setEditing(a); setSheetOpen(true); };

  return (
    <div className="space-y-6" data-testid="agenda-page">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow mb-2">Agenda</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter leading-none">Citas</h1>
        </div>
        <button data-testid="new-appt-btn" onClick={openNew} className="btn-primary">
          <Plus className="w-4 h-4" /> Nueva cita
        </button>
      </header>

      {!cashOpen && (
        <div className="card-flat p-4 border-l-4 border-l-accent flex items-start gap-3" data-testid="caja-closed-banner">
          <Lock className="w-5 h-5 text-accent mt-0.5 shrink-0" />
          <div className="flex-1 text-sm">
            <div className="font-bold mb-1">Caja del día cerrada</div>
            <div className="text-muted-foreground">Puedes agendar citas para días futuros sin abrir caja. Solo necesitas abrir la caja del día para crear citas de hoy o finalizar/cobrar citas.</div>
          </div>
          <Link to="/caja" className="btn-primary !h-9 !px-3 shrink-0" data-testid="caja-closed-banner-go">
            <AlertTriangle className="w-4 h-4" /> Abrir caja
          </Link>
        </div>
      )}

      <div className="card-flat p-3 flex flex-wrap items-center gap-2">
        <div className="flex border border-border rounded-sm overflow-hidden">
          {["day", "week", "month"].map((v) => (
            <button
              key={v}
              data-testid={`view-${v}`}
              onClick={() => setView(v)}
              className={`px-3 h-9 text-xs uppercase tracking-widest font-bold ${view === v ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
            >
              {v === "day" ? "Día" : v === "week" ? "Semana" : "Mes"}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1">
          <button data-testid="prev-range" onClick={() => move(-1)} className="w-9 h-9 rounded-sm border border-border hover:bg-muted flex items-center justify-center">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button onClick={() => setCursor(new Date())} className="px-3 h-9 rounded-sm border border-border hover:bg-muted text-xs uppercase tracking-widest font-bold">
            Hoy
          </button>
          <button data-testid="next-range" onClick={() => move(1)} className="w-9 h-9 rounded-sm border border-border hover:bg-muted flex items-center justify-center">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 text-sm font-mono text-muted-foreground px-2">
          {view === "day" && cursor.toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
          {view === "week" && fmtRange(range.start, range.end)}
          {view === "month" && cursor.toLocaleDateString("es-ES", { month: "long", year: "numeric" })}
        </div>
        <Filter className="w-4 h-4 text-muted-foreground" />
        <select value={filterDoc} onChange={(e) => setFilterDoc(e.target.value)} className="input-flat w-44 h-9">
          <option value="">Todos los doctores</option>
          {doctors.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="input-flat w-44 h-9">
          <option value="">Todos los estados</option>
          {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
        </select>
      </div>

      {view !== "month" ? (
        <div className={`grid gap-3 ${view === "week" ? "grid-cols-1 md:grid-cols-7" : "grid-cols-1"}`}>
          {days.map((d) => {
            const k = localDateKey(d);
            const list = (apptsByDay[k] || []).sort((a,b) => a.start.localeCompare(b.start));
            const isToday = new Date().toDateString() === d.toDateString();
            const dayOpen = isDayOpen(d);
            const ds = settings?.schedule?.[dayKeyOf(d)];
            return (
              <div key={k} className={`card-flat p-3 min-h-[160px] ${isToday ? "ring-1 ring-primary" : ""} ${!dayOpen ? "bg-muted/40 opacity-70" : ""}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="label-eyebrow">{fmtDay(d)}</div>
                  <div className="flex items-center gap-2">
                    {dayOpen && ds && <span className="text-[9px] font-mono text-muted-foreground">{ds.open}-{ds.close}</span>}
                    {!dayOpen && <span className="text-[9px] uppercase font-bold text-destructive">Cerrado</span>}
                    <span className="text-xs font-mono text-muted-foreground">{list.length}</span>
                  </div>
                </div>
                <div className="space-y-2">
                  {list.length === 0 && (
                    <div className="text-xs text-muted-foreground py-2">{dayOpen ? "Sin citas" : "Día no laborable"}</div>
                  )}
                  {list.map((a) => (
                    <button
                      key={a.id}
                      data-testid={`appt-card-${a.id}`}
                      onClick={() => openEdit(a)}
                      className={`w-full text-left p-2 rounded-sm bg-background border border-border hover:bg-muted stripe-${a.status} transition`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-mono text-[11px] text-muted-foreground">
                          {new Date(a.start).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })}
                        </div>
                        <span className={`text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-sm ${STATUS_BADGE[a.status] || STATUS_BADGE.pendiente}`}>
                          {STATUS_LABEL[a.status]}
                        </span>
                      </div>
                      <div className="text-sm font-semibold truncate mt-0.5">{a.patient?.name}</div>
                      <div className="text-[11px] text-muted-foreground truncate">{a.treatment_name}</div>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <MonthView days={days} apptsByDay={apptsByDay} onPick={openEdit} cursor={cursor} />
      )}

      <AppointmentSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        appointment={editing}
        doctors={doctors}
        treatments={treatments}
        cashOpen={cashOpen}
        onSaved={() => { setSheetOpen(false); load(); }}
        onStatusChange={updateStatus}
      />
    </div>
  );
}

function MonthView({ days, apptsByDay, onPick, cursor }) {
  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const startOffset = (first.getDay() + 6) % 7;
  const cells = Array.from({ length: startOffset }).map(() => null).concat(days);
  while (cells.length % 7 !== 0) cells.push(null);
  return (
    <div className="card-flat overflow-hidden">
      <div className="grid grid-cols-7 border-b border-border">
        {["Lun","Mar","Mié","Jue","Vie","Sáb","Dom"].map((d) => (
          <div key={d} className="label-eyebrow text-center py-2">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {cells.map((c, i) => {
          if (!c) return <div key={i} className="min-h-[100px] border-t border-l border-border bg-muted/30" />;
          const k = localDateKey(c);
          const list = apptsByDay[k] || [];
          const isToday = new Date().toDateString() === c.toDateString();
          return (
            <div key={i} className={`min-h-[100px] border-t border-l border-border p-2 ${isToday ? "bg-primary/5" : ""}`}>
              <div className="text-xs font-mono text-muted-foreground mb-1">{c.getDate()}</div>
              <div className="space-y-1">
                {list.slice(0,3).map((a) => (
                  <button
                    key={a.id}
                    onClick={() => onPick(a)}
                    className={`w-full text-left text-[10px] truncate p-1 rounded-sm bg-background border border-border stripe-${a.status} hover:bg-muted`}
                    data-testid={`m-appt-${a.id}`}
                  >
                    {new Date(a.start).toLocaleTimeString("es-ES",{hour:"2-digit",minute:"2-digit"})} · {a.patient?.name?.split(" ")[0]}
                  </button>
                ))}
                {list.length > 3 && <div className="text-[10px] text-muted-foreground">+ {list.length - 3} más</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
